import type { UiFieldRow } from '@/utils/fieldMappingCatalog'

export type FieldMappingSegment = string
export type SegmentMappingOrigin = 'inherited' | 'override'

export type SegmentUiFieldRow = UiFieldRow & {
  origin?: SegmentMappingOrigin
  defaultOrigin?: SegmentMappingOrigin
  segmentReadOnly?: boolean
}

export function isFieldMappingSegment(value: unknown): value is FieldMappingSegment {
  return typeof value === 'string' && /^[a-z0-9][a-z0-9_-]*$/.test(value)
}

export function buildEffectiveSegmentRows(
  allRows: SegmentUiFieldRow[],
  sparseRows: SegmentUiFieldRow[],
): SegmentUiFieldRow[] {
  const overrides = new Map(sparseRows.map((row) => [row.name, row]))

  return allRows.map((allRow) => {
    const override = overrides.get(allRow.name)
    if (!override || allRow.custom) {
      return {
        ...clone(allRow),
        origin: 'inherited',
        defaultOrigin: 'inherited',
        segmentReadOnly: allRow.custom === true,
      }
    }

    const overrideHasDefault = Object.prototype.hasOwnProperty.call(
      override.policy || {},
      'default_value',
    )
    const overrideHasSources = sourceOrder(override).length > 0
    const policy = clone(allRow.policy || { on_missing: 'fallback' })
    if (overrideHasDefault) {
      policy.default_value = clone(override.policy?.default_value)
    }

    return {
      ...clone(allRow),
      ...clone(override),
      policy,
      values: overrideHasSources
        ? clone(override.values || { source: '', fallback: [] })
        : clone(allRow.values || { source: '', fallback: [] }),
      sources: clone(allRow.sources || []),
      origin: 'override',
      defaultOrigin: overrideHasDefault ? 'override' : 'inherited',
      segmentReadOnly: false,
    }
  })
}

export function buildSparseSegmentSelections(
  rows: SegmentUiFieldRow[],
  allRows: SegmentUiFieldRow[],
): Record<string, string | string[]> {
  const allByName = new Map(allRows.map((row) => [row.name, row]))
  const selections: Record<string, string | string[]> = {}

  for (const row of rows) {
    if (!row?.name || row.custom || row.segmentReadOnly) continue
    const allRow = allByName.get(row.name)
    const current = sourceOrder(row)
    const inherited = sourceOrder(allRow)
    if (current.length === 0 || sameOrder(current, inherited)) continue
    selections[row.name] = current.length === 1 ? current[0] : current.slice(0, 2)
  }

  return selections
}

export function buildSparseSegmentDefaultValues(
  rows: SegmentUiFieldRow[],
  allRows: SegmentUiFieldRow[],
): Record<string, unknown> {
  const allByName = new Map(allRows.map((row) => [row.name, row]))
  const defaultValues: Record<string, unknown> = {}

  for (const row of rows) {
    if (!row?.name || row.custom || row.segmentReadOnly) continue
    const current = normalizedDefaultValue(row)
    const inherited = normalizedDefaultValue(allByName.get(row.name))
    if (current === undefined || sameValue(current, inherited)) continue
    defaultValues[row.name] = current
  }

  return defaultValues
}

export function resetSegmentRowToAll(
  row: SegmentUiFieldRow,
  allRow: SegmentUiFieldRow,
): void {
  row.values = clone(allRow.values || { source: '', fallback: [] })
  row.policy = clone(allRow.policy || { on_missing: 'fallback' })
  row.origin = 'inherited'
  row.defaultOrigin = 'inherited'
}

export function sourceOrder(row: SegmentUiFieldRow | undefined): string[] {
  if (!row) return []
  const primary = String(row.values?.source || '').trim()
  const fallback = String(row.values?.fallback?.[0]?.source || '').trim()
  return [primary, fallback].filter(Boolean)
}

function sameOrder(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index])
}

function normalizedDefaultValue(row: SegmentUiFieldRow | undefined): unknown {
  const raw = row?.policy?.default_value
  if (raw === undefined || raw === null || String(raw).trim() === '') return undefined
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

function sameValue(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right)
}

function clone<T>(value: T): T {
  if (value === undefined) return value
  return JSON.parse(JSON.stringify(value))
}
